export type SupabaseAppTarget = 'home' | 'auto' | 'pet';

type EnvOptions = {
  app?: SupabaseAppTarget;
  optional?: boolean;
};

const withAppSuffix = (key: string, app?: SupabaseAppTarget) =>
  app ? `${key}_${app.toUpperCase()}` : key;

const getEnvValue = (key: string) => {
  switch (key) {
    case 'NEXT_PUBLIC_SUPABASE_URL':
      return process.env.NEXT_PUBLIC_SUPABASE_URL;
    case 'NEXT_PUBLIC_SUPABASE_URL_HOME':
      return process.env.NEXT_PUBLIC_SUPABASE_URL_HOME;
    case 'NEXT_PUBLIC_SUPABASE_URL_AUTO':
      return process.env.NEXT_PUBLIC_SUPABASE_URL_AUTO;
    case 'NEXT_PUBLIC_SUPABASE_URL_PET':
      return process.env.NEXT_PUBLIC_SUPABASE_URL_PET;
    case 'NEXT_PUBLIC_SUPABASE_ANON_KEY':
      return process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    case 'NEXT_PUBLIC_SUPABASE_ANON_KEY_HOME':
      return process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_HOME;
    case 'NEXT_PUBLIC_SUPABASE_ANON_KEY_AUTO':
      return process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_AUTO;
    case 'NEXT_PUBLIC_SUPABASE_ANON_KEY_PET':
      return process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY_PET;
    case 'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY':
      return process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY;
    case 'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY_HOME':
      return process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY_HOME;
    case 'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY_AUTO':
      return process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY_AUTO;
    case 'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY_PET':
      return process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY_PET;
    case 'SUPABASE_URL':
      return process.env.SUPABASE_URL;
    case 'SUPABASE_URL_HOME':
      return process.env.SUPABASE_URL_HOME;
    case 'SUPABASE_URL_AUTO':
      return process.env.SUPABASE_URL_AUTO;
    case 'SUPABASE_URL_PET':
      return process.env.SUPABASE_URL_PET;
    case 'SUPABASE_SERVICE_ROLE_KEY':
      return process.env.SUPABASE_SERVICE_ROLE_KEY;
    case 'SUPABASE_SERVICE_ROLE_KEY_HOME':
      return process.env.SUPABASE_SERVICE_ROLE_KEY_HOME;
    case 'SUPABASE_SERVICE_ROLE_KEY_AUTO':
      return process.env.SUPABASE_SERVICE_ROLE_KEY_AUTO;
    case 'SUPABASE_SERVICE_ROLE_KEY_PET':
      return process.env.SUPABASE_SERVICE_ROLE_KEY_PET;
    default:
      return undefined;
  }
};

const readEnv = (baseKey: string, { app, optional }: EnvOptions = {}) => {
  const envKey = withAppSuffix(baseKey, app);
  const fallbackKey = app ? baseKey : undefined;
  const value = getEnvValue(envKey) ?? (fallbackKey ? getEnvValue(fallbackKey) : undefined);

  if (!value && !optional) {
    const hint = fallbackKey && envKey !== fallbackKey ? ` (or ${fallbackKey})` : '';
    throw new Error(`Missing environment variable: ${envKey}${hint}`);
  }

  return value;
};

export type SupabaseClientEnv = {
  supabaseUrl: string;
  supabaseAnonKey: string;
};

export type SupabaseServiceRoleEnv = {
  supabaseUrl: string;
  supabaseServiceRoleKey: string;
};

const readClientAnonKey = (app?: SupabaseAppTarget) => {
  const anon = readEnv('NEXT_PUBLIC_SUPABASE_ANON_KEY', { app, optional: true });
  const pub = readEnv('NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY', { app, optional: true });
  const value = anon ?? pub;
  if (!value) {
    const suffix = app ? ` (or NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY_${app.toUpperCase()})` : ' (or NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY)';
    throw new Error(`Missing environment variable: NEXT_PUBLIC_SUPABASE_ANON_KEY${suffix}`);
  }
  return value;
};

const readRequiredEnv = (key: string, options?: EnvOptions) => {
  const value = readEnv(key, options);
  if (!value) {
    throw new Error(`Missing environment variable: ${withAppSuffix(key, options?.app)}`);
  }
  return value;
};

export const getSupabaseClientEnv = (app?: SupabaseAppTarget): SupabaseClientEnv => ({
  supabaseUrl: readRequiredEnv('NEXT_PUBLIC_SUPABASE_URL', { app }),
  supabaseAnonKey: readClientAnonKey(app),
});

export const getSupabaseServiceRoleEnv = (app?: SupabaseAppTarget): SupabaseServiceRoleEnv => ({
  supabaseUrl: readRequiredEnv('SUPABASE_URL', { app }),
  supabaseServiceRoleKey: readRequiredEnv('SUPABASE_SERVICE_ROLE_KEY', { app }),
});

export const hasSupabaseClientEnv = (app?: SupabaseAppTarget) => {
  const url = readEnv('NEXT_PUBLIC_SUPABASE_URL', { app, optional: true });
  const key = readEnv('NEXT_PUBLIC_SUPABASE_ANON_KEY', { app, optional: true });
  const pub = readEnv('NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY', { app, optional: true });

  return Boolean(url && (key || pub));
};
